import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
} from "@nextui-org/react";
import CusorAiIcon from "assets/svgs/cusor-ai.svg";
import EmptySvg from "assets/svgs/empty.svg";
import { AILoader } from "components/preloader";
import { successToast, warningToast } from "lib/toast";
import { useState } from "react";
import { useSelector } from "react-redux";
import { AIService } from "services/ai.service";
import { selectRelatedCategoryData } from "store/product/product.selector";
import { Product } from "utils/types/products";

type DescriptionProps = {
  product: Product;
  handleApply: (string: string) => void;
};

function ProductDescriptionAiFeature({
  product,
  handleApply,
}: DescriptionProps) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestions] = useState("");
  const relatedData = useSelector(selectRelatedCategoryData);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const aiService = AIService.getInstance();

  const generateAISuggestion = async () => {
    try {
      setLoading(true);
      onOpen();
      const suggestion = await aiService.generateProductDescription(
        product.name,
        relatedData?.name ?? "",
        product.description
      );

      if (suggestion) {
        setSuggestions(suggestion);
        successToast(`AI-generated description applied`);
      }
    } finally {
      setLoading(false);
    }
  };

  const useSuggestion = () => {
    if (suggestion.length === 0) {
      warningToast("Suggestion is empty");
      return false;
    }
    handleApply(suggestion);
    onOpenChange();
  };

  return (
    <>
      <Button
        isIconOnly
        variant="light"
        size="sm"
        isLoading={loading}
        onPress={() => generateAISuggestion()}
      >
        <CusorAiIcon className="w-5 h-5 text-primary dark:text-primary-600 group-hover:text-secondary" />
      </Button>
      <Modal
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        scrollBehavior="inside"
        size="5xl"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Ai Featured Suggestion
              </ModalHeader>
              <ModalBody>
                {suggestion.length > 0 && !loading ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: suggestion }}
                    id="desc-preview"
                    className="space-y-2 p-2 border-1 border-dashed border-gray-200 dark:border-gray-400 rounded-lg"
                  />
                ) : (
                  <div className="centered h-fit">
                    {loading ? <AILoader /> : <EmptySvg className="w-72" />}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Close
                </Button>
                <Button
                  color="primary"
                  onPress={useSuggestion}
                  isDisabled={suggestion.length === 0}
                >
                  Apply Change
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

export default ProductDescriptionAiFeature;
